import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, TextField, InputAdornment, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider,
  ToggleButton, ToggleButtonGroup, Stack, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaidIcon from '@mui/icons-material/Paid';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import DateRangePicker from '../../components/common/DateRangePicker';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const STATUS_FILTERS = ['All', 'Submitted', 'In Review', 'Pending Document', 'Pending Bank Decision', 'Approved', 'Rejected', 'Disbursed', 'Closed'];

const isoDate = (d) => {
  if (typeof d === 'string') return d;
  return new Date(d).toISOString().slice(0, 10);
};
const formatRM = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { maximumFractionDigits: 2 })}`;

export default function ApplicationStatus() {
  const { user } = useAuth();
  const isPartner = user?.role === 'partner';
  const isFreeUser = user?.role === 'free_user';

  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('all'); // all | mine | downline (partner only)
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 89);
    return { start: isoDate(start), end: isoDate(end) };
  });
  const [selectedCase, setSelectedCase] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(new Date());

  const allUsers = useMemo(() => db.users.getAll(), [refreshKey]);
  const userById = useMemo(() => {
    const map = {};
    allUsers.forEach((u) => { map[u.id] = u; });
    return map;
  }, [allUsers]);

  // Determine which cases this user can see
  const visibleCases = useMemo(() => {
    if (!user) return [];
    let cases = [];
    if (isFreeUser) {
      // Free user: only own applications
      cases = db.cases.getAll().filter((c) => c.applicantId === user.id || c.referralOwnerId === user.id);
    } else if (isPartner) {
      // Partner: own + downline cases
      cases = db.cases.getForPartner(user.id, true);
    } else {
      // Admin/agent fallback: all
      cases = db.cases.getAll();
    }
    // Tag each case with source (self vs downline)
    return cases.map((c) => {
      let source = 'self';
      if (isPartner && c.referralOwnerId !== user.id) {
        source = 'downline';
      }
      return {
        ...c,
        source,
        referrerName: userById[c.referralOwnerId]?.name || c.referralOwnerId,
      };
    });
  }, [user, isFreeUser, isPartner, userById, refreshKey]);

  // Apply filters
  const filteredCases = useMemo(() => {
    const startMs = new Date(dateRange.start).getTime();
    const endMs = new Date(dateRange.end + 'T23:59:59').getTime();
    return visibleCases.filter((c) => {
      // Date range
      const t = new Date(c.createdAt).getTime();
      if (t < startMs || t > endMs) return false;
      // Status
      if (statusFilter !== 'All' && c.status !== statusFilter) return false;
      // Source filter (partner only)
      if (isPartner && sourceFilter === 'mine' && c.source !== 'self') return false;
      if (isPartner && sourceFilter === 'downline' && c.source !== 'downline') return false;
      // Search
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const matches =
          c.applicantName?.toLowerCase().includes(s) ||
          c.productName?.toLowerCase().includes(s) ||
          c.bank?.toLowerCase().includes(s) ||
          c.id?.toLowerCase().includes(s) ||
          c.referrerName?.toLowerCase().includes(s) ||
          String(c.amount || '').includes(s);
        if (!matches) return false;
      }
      return true;
    });
  }, [visibleCases, dateRange, statusFilter, sourceFilter, searchTerm, isPartner]);

  // Stats
  const stats = useMemo(() => {
    const approved = filteredCases.filter((c) => ['Approved', 'Disbursed'].includes(c.status));
    const pending = filteredCases.filter((c) => ['Submitted', 'In Review', 'Pending Document', 'Pending Bank Decision'].includes(c.status));
    const disbursed = filteredCases.filter((c) => c.status === 'Disbursed');
    const downline = filteredCases.filter((c) => c.source === 'downline');
    return {
      total: filteredCases.length,
      approved: approved.length,
      pending: pending.length,
      disbursed: disbursed.length,
      downline: downline.length,
    };
  }, [filteredCases]);

  const handleSimulateSync = () => {
    // Delegate to the engine — advances 2-3 random cases with full cascade
    // (commission calculation, token rewards, notifications, audit logs)
    const results = db.engine.simulateImortgageSync({
      id: 'IMORTGAGE2U',
      name: 'iMortgage2u API',
      role: 'system',
    });
    setLastSyncAt(new Date());
    setRefreshKey((k) => k + 1);
    // Show summary of what happened
    if (results.length > 0) {
      const summary = results
        .map((r) => `${r.applicantName} (${r.oldStatus} → ${r.newStatus})`)
        .join(', ');
      // eslint-disable-next-line no-console
      console.info(`iMortgage2u sync: ${results.length} cases advanced — ${summary}`);
    }
  };

  const getProcessedDate = (row) => {
    if (['Approved', 'Disbursed', 'Rejected'].includes(row.status)) return isoDate(row.updatedAt);
    return '—';
  };

  // ─── Columns ────────────────────────────────────────────
  const columns = [
    {
      field: 'id', headerName: 'Case ID', width: 130,
      renderCell: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: PRIMARY }}>
          {row.id}
        </Typography>
      ),
    },
    {
      field: 'applicantName', headerName: 'Customer',
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.applicantName}</Typography>
          {isPartner && (
            <Chip
              icon={row.source === 'self' ? <PersonIcon sx={{ fontSize: 12 }} /> : <GroupsIcon sx={{ fontSize: 12 }} />}
              label={row.source === 'self' ? 'Self' : `Downline · ${row.referrerName}`}
              size="small"
              sx={{
                mt: 0.25, height: 18, fontSize: '0.6rem', fontWeight: 600,
                backgroundColor: row.source === 'self' ? '#E3F2FD' : '#F3E5F5',
                color: row.source === 'self' ? '#1565C0' : '#7B1FA2',
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'productName', headerName: 'Product',
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.productName}</Typography>
          <Typography variant="caption" color="text.secondary">{row.bank}</Typography>
        </Box>
      ),
    },
    {
      field: 'amount', headerName: 'Loan Amount', align: 'right', width: 130,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY }}>
          {formatRM(row.amount)}
        </Typography>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 160,
      renderCell: (row) => <StatusBadge status={row.status} />,
    },
    {
      field: 'createdAt', headerName: 'Created', width: 110,
      renderCell: (row) => <Typography variant="caption">{isoDate(row.createdAt)}</Typography>,
    },
    {
      field: 'processedDate', headerName: 'Processed', width: 110,
      renderCell: (row) => <Typography variant="caption">{getProcessedDate(row)}</Typography>,
    },
    {
      field: 'actions', headerName: '', width: 60, sortable: false,
      renderCell: (row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedCase(row); }}>
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            {isPartner ? 'Application Case' : 'My Applications'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isPartner
              ? 'Read-only view of own and downline cases — status updates flow from iMortgage2u'
              : 'Track the progress of your applications'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Last synced: {lastSyncAt.toLocaleTimeString()}
          </Typography>
          <Button startIcon={<RefreshIcon />} size="small" onClick={handleSimulateSync}
            sx={{ textTransform: 'none', borderRadius: 2 }}>
            Sync iMortgage2u
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={isPartner ? 2.4 : 3}>
          <StatCard title="Total Cases" value={stats.total} icon={AssignmentIcon} color={PRIMARY} />
        </Grid>
        <Grid item xs={6} md={isPartner ? 2.4 : 3}>
          <StatCard title="Pending" value={stats.pending} icon={HourglassEmptyIcon} color="#F57F17" />
        </Grid>
        <Grid item xs={6} md={isPartner ? 2.4 : 3}>
          <StatCard title="Approved" value={stats.approved} icon={CheckCircleIcon} color="#00B894" />
        </Grid>
        <Grid item xs={6} md={isPartner ? 2.4 : 3}>
          <StatCard title="Disbursed" value={stats.disbursed} icon={PaidIcon} color="#2E7D32" />
        </Grid>
        {isPartner && (
          <Grid item xs={12} md={2.4}>
            <StatCard title="Downline" value={stats.downline} icon={GroupsIcon} color="#7B1FA2" />
          </Grid>
        )}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <TextField
              size="small" placeholder="Search customer, product, case ID..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ flex: 1, minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            {isPartner && (
              <ToggleButtonGroup value={sourceFilter} exclusive size="small"
                onChange={(_, v) => v && setSourceFilter(v)}>
                <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>All</ToggleButton>
                <ToggleButton value="mine" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Mine Only</ToggleButton>
                <ToggleButton value="downline" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Downline Only</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map((s) => (
              <Chip key={s} label={s} size="small"
                onClick={() => setStatusFilter(s)}
                sx={{
                  fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer',
                  backgroundColor: statusFilter === s ? PRIMARY : '#E0E0E0',
                  color: statusFilter === s ? 'white' : '#333',
                  '&:hover': { backgroundColor: statusFilter === s ? '#142847' : '#BDBDBD' },
                }}
              />
            ))}
          </Box>
        </Stack>
      </Paper>

      {/* Table */}
      <DataTable
        columns={columns} rows={filteredCases}
        title={isPartner ? `${sourceFilter === 'all' ? 'All' : sourceFilter === 'mine' ? 'My' : 'Downline'} Cases` : 'My Applications'}
        searchable={false} pageSize={15}
        onRowClick={(row) => setSelectedCase(row)}
        exportable
      />

      {/* Empty state */}
      {filteredCases.length === 0 && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          No cases match your filters. Try expanding the date range or clearing search filters.
        </Alert>
      )}

      {/* Case detail dialog */}
      <Dialog open={!!selectedCase} onClose={() => setSelectedCase(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Case Details</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {selectedCase?.id}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCase && (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Customer</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.applicantName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusBadge status={selectedCase.status} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Product</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.productName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Bank</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.bank}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Loan Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY }}>{formatRM(selectedCase.amount)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Commission</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: SECONDARY }}>{formatRM(selectedCase.commissionAmount)}</Typography>
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Source</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedCase.source === 'self' ? 'Direct submission' : `Downline: ${selectedCase.referrerName}`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Referral Code</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCase.referralCode || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(selectedCase.createdAt).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(selectedCase.updatedAt).toLocaleString()}</Typography>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="caption">
                  This view is read-only. Status updates are pushed automatically by iMortgage2u.
                </Typography>
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCase(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
